import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  category: string;
  isVerified: boolean;
}

interface UsageOverview {
  planType: string;
  apiRequests: { used: number; limit: number | null };
  tokens: { used: number; limit: number | null };
}

interface UsageWarning {
  type: 'warning' | 'critical' | 'exceeded';
  service: string;
  used: number;
  limit: number;
  percentage: number;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, currentPage }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [usageOverview, setUsageOverview] = useState<UsageOverview | null>(null);
  const [usageWarning, setUsageWarning] = useState<UsageWarning | null>(null);

  useEffect(() => {
    loadUserData();
    loadUsageOverview();
  }, []);

  const loadUserData = () => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const loadUsageOverview = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/usage/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        const services = data.data.services;
        setUsageOverview({
          planType: data.data.subscription.planType,
          apiRequests: {
            used: services.API_REQUESTS?.used || 0,
            limit: services.API_REQUESTS?.limit || null,
          },
          tokens: {
            used: services.TOKENS?.used || 0,
            limit: services.TOKENS?.limit || null,
          },
        });

        // Check for usage warnings
        for (const [serviceName, service] of Object.entries(services)) {
          const serviceData = service as any;
          if (serviceData.limit && serviceData.percentageUsed > 75) {
            setUsageWarning({
              type: serviceData.percentageUsed > 90 ? 'critical' : 'warning',
              service: serviceName.replace('_', ' ').toLowerCase(),
              used: serviceData.used,
              limit: serviceData.limit,
              percentage: serviceData.percentageUsed
            });
            break;
          }
        }
      }
    } catch (error) {
      console.error("Failed to load usage overview:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getPlanBadgeColor = (planType: string): string => {
    switch (planType?.toLowerCase()) {
      case "premium":
        return "bg-gradient-to-r from-purple-600 to-purple-800 text-white";
      case "business":
        return "bg-gradient-to-r from-blue-600 to-blue-800 text-white";
      case "enterprise":
        return "bg-gradient-to-r from-green-600 to-green-800 text-white";
      default:
        return "bg-gradient-to-r from-gray-600 to-gray-800 text-white";
    }
  };

  const getUsagePercentage = (used: number, limit: number | null): number => {
    if (limit === null) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getWarningColor = (type: string) => {
    switch (type) {
      case 'warning': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'exceeded': return 'bg-red-200 border-red-400 text-red-900';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/',
      icon: '🏠',
      description: 'Overview & stats'
    },
    {
      name: 'AI Chat',
      href: '/chat',
      icon: '💬',
      description: 'Chat with AI assistant'
    },
    {
      name: 'Script Generator',
      href: '/script-generator',
      icon: '⚡',
      description: 'Create chat widgets'
    },
    {
      name: 'Usage Analytics',
      href: '/usage',
      icon: '📊',
      description: 'View detailed stats'
    },
    // {
    //   name: 'API Keys',
    //   href: '/api-keys',
    //   icon: '🔑',
    //   description: 'Manage API access'
    // },
    {
      name: 'Subscription',
      href: '/subscription',
      icon: '💳',
      description: 'Manage your plan'
    }
  ];

  const isActivePage = (href: string) => {
    if (href === '/' && location.pathname === '/') return true;
    if (href !== '/' && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <div className="h-screen bg-white/80 backdrop-blur-lg border-r border-white/20 flex flex-col shadow-xl">
      {/* Sidebar Header */}
      <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">🚀</span>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">AI Dashboard</h2>
              <p className="text-indigo-100 text-sm">Smart Assistant</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-white/80 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* User Info */}
        {user && (
          <Link
            to="/profile"
            onClick={onClose}
            className="flex items-center space-x-3 bg-white/10 rounded-xl p-3 hover:bg-white/20 transition-all duration-200"
          >
            <div className="w-10 h-10 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt="Profile" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-semibold">
                  {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {user.name || user.email.split("@")[0]}
              </p>
              <p className="text-indigo-100 text-xs truncate">
                {user.email}
              </p>
            </div>
          </Link>
        )}
      </div>

      {/* Usage Warning Alert */}
      {usageWarning && (
        <div className="p-4 flex-shrink-0">
          <div className={`p-3 rounded-lg border ${getWarningColor(usageWarning.type)}`}>
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-sm">⚠️</span>
              <span className="text-xs font-medium">Usage Alert</span>
            </div>
            <p className="text-xs">
              {usageWarning.service}: {Math.round(usageWarning.percentage)}% used
            </p>
            <Link
              to="/subscription"
              onClick={onClose}
              className="text-xs underline mt-1 inline-block"
            >
              Upgrade plan →
            </Link>
          </div>
        </div>
      )}

      {/* Plan Info */}
      {usageOverview && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex-shrink-0">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">💎</span>
              </div>
              <span className="text-sm font-medium text-gray-700">Current Plan</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPlanBadgeColor(usageOverview.planType)}`}>
              {usageOverview.planType}
            </span>
          </div>
          
          {/* Quick Usage Stats */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600">
              <span>API Requests</span>
              <span>{usageOverview.apiRequests.used}/{usageOverview.apiRequests.limit || '∞'}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                style={{
                  width: `${getUsagePercentage(usageOverview.apiRequests.used, usageOverview.apiRequests.limit)}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {navigationItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                isActivePage(item.href)
                  ? 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 shadow-md border-l-4 border-indigo-500'
                  : 'text-gray-600 hover:bg-white/60 hover:text-indigo-600 hover:shadow-sm'
              }`}
            >
              <span className="text-lg mr-3">{item.icon}</span>
              <div className="flex-1">
                <div className="font-medium">{item.name}</div>
                <div className="text-xs opacity-70">{item.description}</div>
              </div>
              {isActivePage(item.href) && (
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
        <Link
          to="/usage"
          onClick={onClose}
          className="flex items-center text-indigo-600 hover:text-indigo-700 text-sm mb-3 p-2 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
        >
          <span className="mr-2">📈</span>
          View Analytics →
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center w-full text-red-600 hover:text-red-700 text-sm p-2 rounded-lg hover:bg-red-50 transition-colors font-medium"
        >
          <span className="mr-2">🚪</span>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;